/*
 * Options for planbook backgrounds (separated by category) and patterns.
 *
 * Note: These lists don't include the legacy backgrounds, which don't have file extensions
 * (e.g. "cover-gradient-pink-yellow").
 * That works out because the legacy backgrounds' hosted versions also don't have file extensions---
 * we can use the same URL logic in get-cover-styles for legacy backgrounds and the ones below.
 */

export function parseCover(cover: string): [background: string, pattern: string | null] {
  if (cover.startsWith("http")) {
    // Custom image URL for the background.
    return [cover, null]
  } else {
    const [background, pattern] = cover.split(":")
    return [background, pattern || null]
  }
}

export function serializeCover(background: string, pattern: string | null | undefined): string {
  if (pattern) {
    if (background.startsWith("http")) {
      throw new Error("Cannot use a pattern with a custom background")
    }
    return `${background}:${pattern}`
  } else return background
}

export const gradientBackgrounds = [
  "gradient-blue-purple.svg",
  "gradient-coral-pink.svg",
  "gradient-coral-purple.svg",
  "gradient-cyan-green.svg",
  "gradient-cyan-purple.svg",
  "gradient-dark-coral-dark-purple.svg",
  "gradient-orange-yellow.svg",
  "gradient-pink-yellow.svg",
  "gradient-yellow-green.svg",
]

export const solidColorBackgrounds = [
  "neutral-gray-50.svg",
  "neutral-gray-100.svg",
  "neutral-gray-200.svg",
  "neutral-gray-400.svg",
]

export const imageBackgrounds = [
  "aurora-borealis.webp",
  "birds.webp",
  "blueberries.webp",
  "books.webp",
  "cat-glasses.webp",
  "coral-reef.webp",
  "dark-wavy.webp",
  "hill.webp",
  "mt-fuji.webp",
  "pug.webp",
  "raspberries.webp",
  "retriever-flag.webp",
  "rock.webp",
  "stars.webp",
  "terrier.webp",
  "trees.webp",
  "water-reflections.webp",
  "wood.webp",
  "yawning-cat.webp",
]

export const heroPatterns = [
  "anchorsAway.svg",
  "architect.svg",
  "autumn.svg",
  "aztec.svg",
  "bamboo.svg",
  "bankNote.svg",
  "bathroomFloor.svg",
  "bevelCircle.svg",
  "boxes.svg",
  "brickWall.svg",
  "bubbles.svg",
  "cage.svg",
  "charlieBrown.svg",
  "churchOnSunday.svg",
  "circlesAndSquares.svg",
  "circuitBoard.svg",
  "connections.svg",
  "corkScrew.svg",
  "current.svg",
  "curtain.svg",
  "cutout.svg",
  "deathStar.svg",
  "diagonalLines.svg",
  "diagonalStripes.svg",
  "dominos.svg",
  "endlessClouds.svg",
  "eyes.svg",
  "fallingTriangles.svg",
  "fancyRectangles.svg",
  "flippedDiamonds.svg",
  "floatingCogs.svg",
  "floorTile.svg",
  "formalInvitation.svg",
  "fourPointStars.svg",
  "glamorous.svg",
  "graphPaper.svg",
  "groovy.svg",
  "happyIntersection.svg",
  "heavyRain.svg",
  "hexagons.svg",
  "hideout.svg",
  "houndstooth.svg",
  "iLikeFood.svg",
  "intersectingCircles.svg",
  "jigsaw.svg",
  "jupiter.svg",
  "kiwi.svg",
  "leaf.svg",
  "linesInMotion.svg",
  "lips.svg",
  "lisbon.svg",
  "melt.svg",
  "moroccan.svg",
  "morphingDiamonds.svg",
  "overcast.svg",
  "overlappingCircles.svg",
  "overlappingDiamonds.svg",
  "overlappingHexagons.svg",
  "parkayFloor.svg",
  "pianoMan.svg",
  "pieFactory.svg",
  "pixelDots.svg",
  "plus.svg",
  "polkaDots.svg",
  "rails.svg",
  "rain.svg",
  "randomShapes.svg",
  "roundedPlusConnected.svg",
  "signal.svg",
  "slantedStars.svg",
  "squares.svg",
  "squaresInSquares.svg",
  "stampCollection.svg",
  "steelBeams.svg",
  "stripes.svg",
  "temple.svg",
  "texture.svg",
  "ticTacToe.svg",
  "tinyCheckers.svg",
  "topography.svg",
  "volcanoLamp.svg",
  "wallpaper.svg",
  "wiggle.svg",
  "xEquals.svg",
  "yyy.svg",
  "zigZag.svg",
] as const

/**
 * The initial pattern to use when the user chooses a color background.
 * The user can later remove the pattern (set it to null).
 */
export const initialPatternForColor = "bankNote.svg" satisfies (typeof heroPatterns)[number]
